/**
 * Frontier run detail — status, provenance, ledger, steps and events
 * (REQ-FRT-016 §3). Actions: cancel/resume per the action × state matrix.
 */

import { Link, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { StatusChip, formatDateTime } from '../components/FrontierGuard';
import { frontierEventLabel } from '../lib/eventLabels';
import { useFrontierRun, useRetryRun, useRunActions } from '../hooks/useFrontier';
import { RUN_CANCELLABLE, RUN_RESUMABLE } from '../types';

const RETRYABLE = ['failed', 'emergency_stopped', 'cancelled', 'expired'];

function provenanceLabel(
  source: string | undefined,
  t: (key: string) => string,
): string {
  if (!source) return '';
  const parts = source
    .split(/[+,]/)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts
    .map((part) => {
      const key = `frontier.sources.${part}`;
      const translated = t(key);
      return translated === key ? part : translated;
    })
    .join(' ');
}

export default function FrontierRunDetailPage() {
  const { t } = useI18n();
  const { runId } = useParams<{ runId: string }>();
  const canOperate = useModuleAccess('frontier', 'operate');
  const { data, isLoading, isError } = useFrontierRun(runId);
  const actions = useRunActions();
  const retry = useRetryRun();

  if (isLoading) {
    return (
      <div className="p-6">
        <Spinner />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-6 space-y-3" data-testid="frontier-run-notfound">
        <p className="text-sm text-red-600" role="alert">
          {t('frontier.run.notFound')}
        </p>
        <Link className="text-sm text-blue-600 hover:underline" to="/frontier/runs">
          {t('frontier.run.back')}
        </Link>
      </div>
    );
  }

  const values = data.effective_params?.values ?? {};
  const trace = data.effective_params?.source_trace ?? {};
  const memory = data.memory;

  return (
    <div className="p-6 space-y-4" data-testid="frontier-run-detail">
      <div className="flex items-center justify-between">
        <div>
          <Link className="text-sm text-blue-600 hover:underline" to="/frontier/runs">
            {t('frontier.run.back')}
          </Link>
          <h1 className="mt-1 font-mono text-xl">{data.id}</h1>
        </div>
        <div className="flex items-center gap-2">
          <StatusChip kind="run" value={data.status} label={t(`frontier.runStatus.${data.status}`)} />
          <Button
            size="sm"
            variant="destructive"
            disabled={!canOperate || !RUN_CANCELLABLE.includes(data.status)}
            title={RUN_CANCELLABLE.includes(data.status) ? undefined : t('frontier.routines.actions.phase')}
            data-testid="run-cancel"
            onClick={() => actions.mutate({ runId: data.id, action: 'cancel' })}
          >
            {t('frontier.run.cancel')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!canOperate || !RUN_RESUMABLE.includes(data.status)}
            title={RUN_RESUMABLE.includes(data.status) ? undefined : t('frontier.routines.actions.phase')}
            data-testid="run-resume"
            onClick={() => actions.mutate({ runId: data.id, action: 'resume' })}
          >
            {t('frontier.run.resume')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!canOperate || !RETRYABLE.includes(data.status)}
            title={RETRYABLE.includes(data.status) ? undefined : t('frontier.routines.actions.phase')}
            data-testid="run-retry"
            onClick={() => retry.mutate(data.id)}
          >
            {t('frontier.run.retry')}
          </Button>
        </div>
      </div>

      {actions.isError && (
        <p className="text-sm text-red-600" role="alert">
          {t('frontier.actions.failed')}
        </p>
      )}
      {retry.isError && (
        <p className="text-sm text-red-600" role="alert">
          {t('frontier.run.retryError')}
        </p>
      )}
      {retry.data?.run_id && (
        <p className="text-sm text-emerald-600" role="status" data-testid="run-retry-created">
          {t('frontier.run.retryCreated')}: {retry.data.run_id}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('frontier.run.provenance')}</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(values).length === 0 ? (
              <p className="text-sm text-gray-500">{t('frontier.run.provenanceEmpty')}</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="py-1 pr-3 font-medium">{t('frontier.run.param')}</th>
                    <th className="py-1 pr-3 font-medium">{t('frontier.run.value')}</th>
                    <th className="py-1 font-medium">{t('frontier.run.source')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(values).map(([key, value]) => (
                    <tr key={key} data-testid={`provenance-${key}`}>
                      <td className="py-1 pr-3 font-mono text-xs">{key}</td>
                      <td className="py-1 pr-3">{String(value)}</td>
                      <td className="py-1 text-gray-500 dark:text-gray-400">
                        {provenanceLabel(trace[key], t)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('frontier.run.ledger')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {!memory ? (
              <p className="text-gray-500">{t('frontier.run.ledgerEmpty')}</p>
            ) : (
              <>
                <div>
                  <p className="font-medium">{t('frontier.run.facts')}</p>
                  <ul className="list-disc pl-5 text-gray-600 dark:text-gray-300">
                    {Object.entries(memory.facts).map(([key, value]) => (
                      <li key={key}>
                        <span className="font-mono text-xs">{key}</span>: {String(value)}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-medium">{t('frontier.run.assumptions')}</p>
                  <ul className="list-disc pl-5 text-gray-600 dark:text-gray-300">
                    {memory.assumptions.map((item, index) => (
                      <li key={index}>{String(item)}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-medium">{t('frontier.run.openQuestions')}</p>
                  <ul className="list-disc pl-5 text-gray-600 dark:text-gray-300">
                    {memory.open_questions.map((item, index) => (
                      <li key={index}>{String(item)}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-medium">{t('frontier.run.artifacts')}</p>
                  <ul className="list-disc pl-5 font-mono text-xs text-gray-600 dark:text-gray-300">
                    {memory.artifacts.map((item, index) => (
                      <li key={index}>{JSON.stringify(item)}</li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('frontier.run.checkpoints')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {data.checkpoints.length === 0 ? (
              <p className="text-gray-500">{t('frontier.run.checkpointsEmpty')}</p>
            ) : (
              <ul className="space-y-1">
                {data.checkpoints.map((checkpoint) => (
                  <li key={checkpoint.step} className="flex justify-between">
                    <span className="font-mono text-xs">
                      #{checkpoint.step}
                      {checkpoint.ledger_revision ? ` · r${checkpoint.ledger_revision}` : ''}
                    </span>
                    <span className="text-gray-500">{formatDateTime(checkpoint.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('frontier.run.attempts')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {(data.attempts ?? []).length === 0 ? (
              <p className="text-gray-500">{t('frontier.run.attemptsEmpty')}</p>
            ) : (
              <ul className="space-y-1">
                {(data.attempts ?? []).map((attempt) => (
                  <li
                    key={attempt.attempt}
                    className="flex justify-between"
                    data-testid={`run-attempt-${attempt.attempt}`}
                  >
                    <span className="font-mono text-xs">
                      #{attempt.attempt} · {attempt.status}
                      {attempt.error_class ? ` · ${attempt.error_class}` : ''}
                    </span>
                    <span className="text-gray-500">
                      {attempt.backoff_seconds
                        ? t('frontier.run.attemptBackoff', { seconds: attempt.backoff_seconds })
                        : formatDateTime(attempt.ended_at ?? attempt.started_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('frontier.run.events')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {data.events.length === 0 ? (
              <p className="text-gray-500">{t('frontier.run.eventsEmpty')}</p>
            ) : (
              <ul className="space-y-2">
                {data.events.map((event) => (
                  <li key={event.id} className="border-b border-gray-100 pb-1 dark:border-gray-800">
                    <div className="flex justify-between">
                      <span className="text-xs">{frontierEventLabel(t, event.type)}</span>
                      <span className="text-gray-500">{formatDateTime(event.created_at)}</span>
                    </div>
                    {(data.outcome || data.stop_reason || data.error_code) && (
                      <p className="text-xs text-gray-500">
                        {t('frontier.run.stopReason')}:{' '}
                        {data.stop_reason ?? data.outcome ?? data.error_code}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
